import { useState, useRef, useEffect, useMemo } from "react";
import { CaretDown, MagnifyingGlass, X } from "@phosphor-icons/react";

/**
 * SearchableSelect — rich dropdown with a search bar.
 * Props:
 *   value: current selected value (any)
 *   onChange: (newValue) => void
 *   options: [{ value, label, sub? }]   (sub = secondary line shown in muted text)
 *   placeholder, disabled, clearable, testId
 */
export default function SearchableSelect({
  value, onChange, options = [], placeholder = "Select…",
  disabled = false, clearable = false, testId, className = "", onOpen,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (inputRef.current) inputRef.current.focus();
    if (onOpen) onOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const current = useMemo(() => options.find((o) => o.value === value), [options, value]);
  const filtered = useMemo(() => {
    if (!q) return options;
    const s = q.toLowerCase();
    return options.filter((o) =>
      (o.label || "").toLowerCase().includes(s) || (o.sub || "").toLowerCase().includes(s) || String(o.value || "").toLowerCase().includes(s));
  }, [options, q]);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        data-testid={testId}
        className={`w-full flex items-center justify-between gap-2 border border-slate-300 dark:border-slate-700 rounded-md px-3 py-2 text-sm bg-white dark:bg-slate-900
          ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-[#fd7e14]"}`}
      >
        <span className={`truncate text-left flex-1 ${current ? "" : "opacity-50"}`}>
          {current ? current.label : placeholder}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {clearable && current && !disabled && (
            <span onClick={(e) => { e.stopPropagation(); onChange(""); }} className="hover:text-red-600 cursor-pointer p-0.5"><X size={12} /></span>
          )}
          <CaretDown size={14} className="opacity-60" />
        </div>
      </button>

      {open && (
        <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg z-50 max-h-72 flex flex-col overflow-hidden">
          <div className="border-b border-slate-200 dark:border-slate-700 flex items-center gap-2 px-2 py-1.5">
            <MagnifyingGlass size={14} className="opacity-50" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="bg-transparent outline-none text-sm flex-1"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && <div className="px-3 py-6 text-xs text-center opacity-60">No matches</div>}
            {filtered.map((o) => (
              <button
                type="button"
                key={o.value}
                onClick={() => { onChange(o.value); setOpen(false); setQ(""); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800
                  ${o.value === value ? "bg-orange-50 dark:bg-orange-950/30 text-[#fd7e14]" : ""}`}
                data-testid={testId ? `${testId}-opt-${o.value}` : undefined}
              >
                <div className="truncate">{o.label}</div>
                {o.sub && <div className="text-xs opacity-60 truncate">{o.sub}</div>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
